import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, FileText, Loader2, Eye, Globe, Power, PowerOff, RefreshCw } from 'lucide-react';
import DocumentSearch from '@/components/DocumentSearch';
import ResizableDataTable from '@/components/ResizableDataTable';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { getStatusBadge } from '@/lib/dashboardUtils';

export default function DokumenteTab({ isDark, userInfo, selectedMunicipalityId, municipalitiesList = [] }) {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fetchUrlDialogOpen, setFetchUrlDialogOpen] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateConflict, setDuplicateConflict] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [overviewFilters, setOverviewFilters] = useState({ municipality_id: '', status: '', type: '' });
  const [inspectOpen, setInspectOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docChunks, setDocChunks] = useState([]);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ files: [], title: '', docDate: '', sourceUrl: '' });
  const [fetchUrlForm, setFetchUrlForm] = useState({ url: '' });

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const params = {};
      if (userInfo?.role === 'ProviderAdmin') {
        params.limit = 500;
        const muniFilter = selectedMunicipalityId || overviewFilters.municipality_id;
        if (muniFilter) params.municipality_id = muniFilter;
        if (overviewFilters.status) params.status = overviewFilters.status;
        if (overviewFilters.type) params.type = overviewFilters.type;
      }
      const response = await apiClient.get('/docs/list', Object.keys(params).length ? { params } : {});
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching docs:', error);
      toast.error('Fehler beim Laden der Dokumente');
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    if (userInfo) fetchDocuments();
  }, [userInfo?.role, selectedMunicipalityId]);

  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin') fetchDocuments();
  }, [overviewFilters.municipality_id, overviewFilters.status, overviewFilters.type]);

  const fetchChunks = async (doc) => {
    setSelectedDoc(doc);
    setInspectOpen(true);
    setLoadingChunks(true);
    try {
      const response = await apiClient.get(`/docs/${doc.id}/chunks`);
      setDocChunks(response.data);
    } catch (error) {
      console.error('Error fetching chunks:', error);
      toast.error('Fehler beim Laden der Text-Bausteine');
    } finally {
      setLoadingChunks(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      setUploadForm(prev => ({ ...prev, files: selectedFiles, title: selectedFiles.length === 1 ? selectedFiles[0].name.replace(/\.[^/.]+$/, '') : '' }));
    }
  };

  const handleUpload = async (e, strategy = null) => {
    e?.preventDefault();
    if (uploadForm.files.length === 0) { toast.error('Bitte mindestens eine Datei auswählen'); return; }
    if (uploadForm.files.length === 1 && !uploadForm.title.trim()) { toast.error('Bitte Titel angeben'); return; }
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (const file of uploadForm.files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const docTitle = uploadForm.files.length === 1 ? uploadForm.title.trim() : file.name.replace(/\.[^/.]+$/, '');
        formData.append('title', docTitle);
        if (uploadForm.docDate) formData.append('doc_date', uploadForm.docDate);
        if (uploadForm.sourceUrl) formData.append('source_url', uploadForm.sourceUrl.trim());
        if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) { formData.append('municipality_id', selectedMunicipalityId); }
        else if (userInfo?.municipality_id) { formData.append('municipality_id', userInfo.municipality_id); }
        const uploadUrl = strategy ? `/docs/upload?strategy=${strategy}` : '/docs/upload';
        await apiClient.post(uploadUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        if (error.response?.status === 409) {
          const conflictData = error.response?.data?.detail;
          if (conflictData?.existing_doc || conflictData?.error === 'Duplicate document') {
            setDuplicateConflict({ existingDoc: conflictData.existing_doc || conflictData, fileName: file.name });
            setPendingUpload({ type: 'upload', file: file, formData: { title: uploadForm.files.length === 1 ? uploadForm.title.trim() : file.name.replace(/\.[^/.]+$/, ''), docDate: uploadForm.docDate, sourceUrl: uploadForm.sourceUrl.trim() } });
            setDuplicateDialogOpen(true);
            setUploading(false);
            return;
          }
        }
        failCount++;
        toast.error(`Fehler beim Hochladen von ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }
    if (failCount === 0 && successCount > 0) {
      toast.success(`${successCount} Dokument(e) erfolgreich hochgeladen`);
      setUploadDialogOpen(false);
      setUploadForm({ files: [], title: '', docDate: '', sourceUrl: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } else if (failCount > 0 && successCount === 0) {
      toast.warning(`${failCount} Dokument(e) fehlgeschlagen`);
    }
    setUploading(false);
  };

  const handleFetchUrl = async (e, strategy = null) => {
    e?.preventDefault();
    if (!fetchUrlForm.url.trim()) return;
    setFetchingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', fetchUrlForm.url.trim());
      if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) { formData.append('municipality_id', selectedMunicipalityId); }
      const fetchUrl = strategy ? `/docs/fetch_url?strategy=${strategy}` : '/docs/fetch_url';
      await apiClient.post(fetchUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('URL wird verarbeitet');
      setFetchUrlDialogOpen(false);
      setFetchUrlForm({ url: '' });
      fetchDocuments();
    } catch (error) {
      console.error('Fetch URL Error:', error);
      if (error.response?.status === 409) {
        const conflictData = error.response?.data?.detail;
        if (conflictData?.existing_doc || conflictData?.error === 'Duplicate document') {
          setDuplicateConflict({ existingDoc: conflictData.existing_doc || conflictData, url: fetchUrlForm.url.trim() });
          setPendingUpload({ type: 'fetchUrl', url: fetchUrlForm.url.trim() });
          setDuplicateDialogOpen(true);
          setFetchingUrl(false);
          return;
        }
      }
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'URL-Abruf fehlgeschlagen';
      toast.error(`URL-Abruf fehlgeschlagen: ${errorMessage}`, { duration: 5000 });
    } finally {
      setFetchingUrl(false);
    }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;
    try { await apiClient.delete(`/docs/${id}`); toast.success('Gelöscht'); fetchDocuments(); }
    catch (error) { toast.error('Löschen fehlgeschlagen'); }
  };

  const handleToggleDoc = async (id, currentStatus) => {
    try { await apiClient.post('/docs/toggle', { document_id: id, is_active: !currentStatus }); toast.success(currentStatus ? 'Deaktiviert' : 'Aktiviert'); fetchDocuments(); }
    catch (error) { toast.error('Fehler beim Ändern des Status'); }
  };

  const handleDuplicateRetry = async () => {
    if (!pendingUpload) return;
    setDuplicateDialogOpen(false);
    setUploading(true);
    setFetchingUrl(true);
    try {
      if (pendingUpload.type === 'upload') {
        const formData = new FormData();
        formData.append('file', pendingUpload.file);
        formData.append('title', pendingUpload.formData.title);
        if (pendingUpload.formData.docDate) formData.append('doc_date', pendingUpload.formData.docDate);
        if (pendingUpload.formData.sourceUrl) formData.append('source_url', pendingUpload.formData.sourceUrl);
        if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) { formData.append('municipality_id', selectedMunicipalityId); }
        else if (userInfo?.municipality_id) { formData.append('municipality_id', userInfo.municipality_id); }
        await apiClient.post('/docs/upload?strategy=new_version', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Neue Version erfolgreich erstellt');
        setUploadDialogOpen(false);
        setUploadForm({ files: [], title: '', docDate: '', sourceUrl: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchDocuments();
      } else if (pendingUpload.type === 'fetchUrl') {
        const formData = new FormData();
        formData.append('url', pendingUpload.url);
        if (userInfo?.role === 'ProviderAdmin' && selectedMunicipalityId) { formData.append('municipality_id', selectedMunicipalityId); }
        await apiClient.post('/docs/fetch_url?strategy=new_version', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Neue Version wird verarbeitet');
        setFetchUrlDialogOpen(false);
        setFetchUrlForm({ url: '' });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error retrying with new_version:', error);
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
      setFetchingUrl(false);
      setPendingUpload(null);
      setDuplicateConflict(null);
    }
  };

  const handleDuplicateCancel = () => { setDuplicateDialogOpen(false); setPendingUpload(null); setDuplicateConflict(null); };

  return (
    <>
      {/* Document Search */}
      <DocumentSearch isDark={isDark} onOpenChunkInspector={fetchChunks} />
      
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Wissensdatenbank</h2>
        {userInfo?.role === 'ProviderAdmin' && (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={overviewFilters.municipality_id || 'all'} onValueChange={(v) => setOverviewFilters(f => ({ ...f, municipality_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className={`w-[200px] ${isDark ? 'bg-slate-900 border-slate-700' : ''}`}><SelectValue placeholder="Gemeinde" /></SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                <SelectItem value="all">Alle Gemeinden</SelectItem>
                {municipalitiesList.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={overviewFilters.status || 'all'} onValueChange={(v) => setOverviewFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className={`w-[160px] ${isDark ? 'bg-slate-900 border-slate-700' : ''}`}><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="indexed">Indiziert</SelectItem>
                <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                <SelectItem value="processing">Verarbeitung</SelectItem>
                <SelectItem value="indexing">Indexierung</SelectItem>
              </SelectContent>
            </Select>
            <Select value={overviewFilters.type || 'all'} onValueChange={(v) => setOverviewFilters(f => ({ ...f, type: v === 'all' ? '' : v }))}>
              <SelectTrigger className={`w-[120px] ${isDark ? 'bg-slate-900 border-slate-700' : ''}`}><SelectValue placeholder="Typ" /></SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
                <SelectItem value="all">Alle Typen</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="html">HTML</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchDocuments} disabled={loadingDocs} className={isDark ? 'border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800' : ''}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingDocs ? 'animate-spin' : ''}`} /> Aktualisieren
            </Button>
          </div>
        )}
        <div className="flex gap-2">
          <Dialog open={fetchUrlDialogOpen} onOpenChange={setFetchUrlDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className={isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}>
                <Globe className="w-4 h-4 mr-2" /> URL abrufen
              </Button>
            </DialogTrigger>
            <DialogContent className={isDark ? "bg-slate-900 border-slate-700 text-white sm:max-w-[500px]" : "bg-white border-slate-300 text-slate-900 sm:max-w-[500px]"}>
              <DialogHeader>
                <DialogTitle>Webseite importieren</DialogTitle>
                <DialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>Inhalte von einer URL werden extrahiert und indexiert.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleFetchUrl} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>URL</Label>
                  <Input value={fetchUrlForm.url} onChange={e => setFetchUrlForm({...fetchUrlForm, url: e.target.value})} placeholder="https://www.gemeinde.de/seite" className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} required />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={fetchingUrl} className="bg-sky-600 hover:bg-sky-500 text-white">
                    {fetchingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Importieren'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white btn-glow border-none">
                <Plus className="w-4 h-4 mr-2" /> Dokument hochladen
              </Button>
            </DialogTrigger>
            <DialogContent className={isDark ? "bg-slate-900 border-slate-700 text-white sm:max-w-[500px]" : "bg-white border-slate-300 text-slate-900 sm:max-w-[500px]"}>
              <DialogHeader>
                <DialogTitle>Dokumente hochladen</DialogTitle>
                <DialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>Laden Sie PDF, HTML oder Text-Dateien hoch. Mehrfachauswahl möglich.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Dateien</Label>
                  <Input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.html,.txt"
                    className={isDark ? "bg-slate-950 border-slate-700 text-white file:bg-slate-800 file:text-white file:border-0" : "bg-white border-slate-300 text-slate-900 file:bg-slate-100 file:text-slate-700 file:border-0"}
                    required multiple />
                  {uploadForm.files.length > 1 && (
                    <p className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>{uploadForm.files.length} Dateien ausgewählt</p>
                  )}
                </div>
                {uploadForm.files.length <= 1 && (
                  <div className="space-y-2">
                    <Label>Titel</Label>
                    <Input value={uploadForm.title} onChange={e => setUploadForm({...uploadForm, title: e.target.value})} placeholder="z.B. Hundesteuersatzung 2025"
                      className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} required={uploadForm.files.length === 1} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum (Optional)</Label>
                    <Input type="date" value={uploadForm.docDate} onChange={e => setUploadForm({...uploadForm, docDate: e.target.value})}
                      className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} />
                  </div>
                  <div className="space-y-2">
                    <Label>Original-URL (Optional)</Label>
                    <Input value={uploadForm.sourceUrl} onChange={e => setUploadForm({...uploadForm, sourceUrl: e.target.value})} placeholder="https://..."
                      className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={uploading} className="bg-sky-600 hover:bg-sky-500 text-white">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : uploadForm.files.length > 1 ? 'Alle hochladen' : 'Hochladen & Indexieren'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className={isDark ? "bg-slate-900 border-slate-800 shadow-lg" : "bg-slate-50 border-slate-200 shadow-lg"}>
        <CardContent className="p-0">
          <ResizableDataTable
            isDark={isDark}
            loading={loadingDocs}
            data={documents.filter(d => !selectedMunicipalityId || d.municipality_id === selectedMunicipalityId)}
            rowKey="id"
            emptyMessage="Keine Dokumente gefunden."
            storageKey="documents"
            columns={[
              { key: 'title', label: 'Titel', width: 220, minWidth: 100, filterType: 'text', filterPlaceholder: 'Filter Titel…', sortable: true, cellTitle: (row) => row.title,
                render: (row) => (<span className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}><FileText className="w-4 h-4 text-sky-500 flex-shrink-0" />{row.title || '—'}</span>) },
              { key: 'source_url', label: 'URL', width: 200, minWidth: 100, filterType: 'text', filterPlaceholder: 'Filter URL…', sortable: true, cellTitle: (row) => row.source_url,
                render: (row) => row.source_url ? (<a href={row.source_url} target="_blank" rel="noopener noreferrer" className={`text-xs ${isDark ? 'text-sky-400 hover:underline' : 'text-sky-600 hover:underline'}`}>{row.source_url}</a>) : '—' },
              { key: 'type', label: 'Typ', width: 90, minWidth: 70, filterType: 'select', filterOptions: [{ value: 'pdf', label: 'PDF' }, { value: 'html', label: 'HTML' }, { value: 'text', label: 'Text' }], sortable: true,
                render: (row) => (<Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-300' : 'border-slate-400 text-slate-700'}>{row.type || '—'}</Badge>) },
              { key: 'status', label: 'Status', width: 130, minWidth: 90, filterType: 'select', filterOptions: [{ value: 'indexed', label: 'Indiziert' }, { value: 'failed', label: 'Fehlgeschlagen' }, { value: 'processing', label: 'Verarbeitung' }, { value: 'indexing', label: 'Indexierung' }, { value: 'uploaded', label: 'Hochgeladen' }], sortable: true,
                render: (row) => (<div className="flex items-center gap-2">{getStatusBadge(row.status)}{!row.is_active && <Badge variant="outline" className={isDark ? 'text-slate-500 border-slate-600' : 'text-slate-600 border-slate-400'}>Inaktiv</Badge>}</div>) },
              ...(userInfo?.role === 'ProviderAdmin' ? [{ key: 'municipality_id', label: 'Gemeinde', width: 130, minWidth: 80, filterType: 'select', filterOptions: municipalitiesList.map(m => ({ value: m.id, label: m.name })), sortable: true,
                render: (row) => (<span className={`text-xs font-mono ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{row.municipality_id || '—'}</span>) }] : []),
              { key: 'chunks_count', label: 'Chunks', width: 80, minWidth: 60, filterType: 'number', filterPlaceholder: 'Min', sortable: true, filterAccessor: (row) => row.chunks_count || 0, sortAccessor: (row) => row.chunks_count || 0,
                render: (row) => (<span className={`font-mono text-xs px-2 py-1 rounded ${isDark ? 'text-slate-300 bg-slate-950' : 'text-slate-700 bg-slate-100'}`}>{row.chunks_count}</span>) },
              { key: 'uploaded_by', label: 'Hochgeladen von', width: 140, minWidth: 90, filterType: 'text', filterPlaceholder: 'Filter…', sortable: true,
                render: (row) => (<span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>{row.uploaded_by}</span>) },
              { key: 'uploaded_at', label: 'Datum', width: 110, minWidth: 80, filterType: 'date', sortable: true, filterAccessor: (row) => row.uploaded_at, sortAccessor: (row) => row.uploaded_at ? new Date(row.uploaded_at).getTime() : 0,
                render: (row) => (<span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{row.uploaded_at ? new Date(row.uploaded_at).toLocaleDateString('de-DE') : '—'}</span>) },
              { key: 'actions', label: 'Aktionen', width: 120, minWidth: 80, filterType: 'none',
                render: (row) => (
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); fetchChunks(row); }} title="Chunks inspizieren"
                      className={isDark ? 'hover:bg-slate-700 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {(userInfo?.role === 'MunicipalityAdmin' || userInfo?.role === 'ProviderAdmin') && (
                      <>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleToggleDoc(row.id, row.is_active); }} title={row.is_active ? 'Deaktivieren' : 'Aktivieren'}
                          className={isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}>
                          {row.is_active ? <PowerOff className="w-4 h-4 text-orange-400" /> : <Power className="w-4 h-4 text-green-400" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteDoc(row.id); }} title="Löschen"
                          className={isDark ? 'hover:bg-red-900/20 text-slate-400 hover:text-red-400' : 'hover:bg-red-50 text-slate-600 hover:text-red-600'}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                ) },
            ]}
          />
        </CardContent>
      </Card>

      {/* CHUNK INSPECTOR SHEET */}
      <Sheet open={inspectOpen} onOpenChange={setInspectOpen}>
        <SheetContent className={isDark 
          ? "w-[600px] sm:w-[800px] bg-slate-900 border-slate-800 text-white border-l" 
          : "w-[600px] sm:w-[800px] bg-white border-slate-300 text-slate-900 border-l"
        }>
          <SheetHeader>
            <SheetTitle className={isDark ? "text-white" : "text-slate-900"}>Text-Bausteine</SheetTitle>
            <SheetDescription className={isDark ? "text-slate-300" : "text-slate-600"}>{selectedDoc?.title}</SheetDescription>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-120px)] mt-4 pr-4">
            {loadingChunks ? (
              <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-500" /></div>
            ) : (
              <div className="space-y-4 pb-10">
                {docChunks.map((chunk, idx) => (
                  <Card key={idx} className={isDark ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <Badge variant="secondary" className={isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}>
                          Chunk #{chunk.metadata?.chunk_index ?? idx}
                        </Badge>
                        <span className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                          {chunk.metadata?.page ? `Seite ${chunk.metadata.page}` : chunk.metadata?.section}
                        </span>
                      </div>
                      <p className={`text-sm font-mono leading-relaxed whitespace-pre-wrap p-2 rounded ${isDark ? "text-slate-200 bg-slate-900/50" : "text-slate-800 bg-slate-100"}`}>
                        {chunk.text}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* DUPLICATE CONFLICT ALERT DIALOG */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? "text-white" : "text-slate-900"}>Dokument existiert bereits</AlertDialogTitle>
            <AlertDialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              Ein Dokument mit diesem Inhalt/URL existiert bereits.
              {duplicateConflict?.existingDoc && (
                <div className={`mt-2 p-2 rounded text-sm ${isDark ? "bg-slate-800 text-slate-200" : "bg-slate-100 text-slate-700"}`}>
                  <p className="font-semibold mb-1">Vorhandenes Dokument:</p>
                  <p>• Titel: {duplicateConflict.existingDoc.title || 'Unbekannt'}</p>
                  {duplicateConflict.existingDoc.uploaded_at && (<p>• Hochgeladen: {new Date(duplicateConflict.existingDoc.uploaded_at).toLocaleDateString('de-DE')}</p>)}
                </div>
              )}
              <p className="mt-3">Möchten Sie eine neue Version erstellen (altes wird archiviert)?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateCancel} className={isDark ? "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateRetry} className="bg-sky-600 hover:bg-sky-500 text-white">
              Neue Version erstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
